/**
 * core · a trilha append-only (spec 03 §2 · spec 02 §1.11 · DS-10).
 *
 * A leitura da auditoria tem **uma** superfície: o painel "Log de alterações" da
 * 8.7. Não existe segunda tela de trilha completa, e este contrato é o único jeito
 * de sair da tabela — a `GET /:recurso/:id/trilha` (spec 03 §2) responde a outra
 * pergunta, a do registro que se está olhando, com a permissão do módulo DELE.
 *
 * Três decisões de forma que valem explicar:
 *
 * 1. **`id` é texto.** A chave é `bigint GENERATED ALWAYS AS IDENTITY`, e bigint
 *    não cabe em `number` do JSON sem perder precisão. Ele serve para chavear a
 *    linha na tela, nunca para aritmética.
 * 2. **`entidadeTipo` é `string`, não enum.** São 42 valores hoje, declarados nas
 *    rotas de mutação, e a lista cresce a cada passo do roteiro. Fechá-la aqui faria
 *    a trilha REJEITAR o fato de uma entidade nova — o oposto de append-only.
 *    O rótulo em voz de produto e o destino do deep-link vivem na tela, que é onde
 *    o GLOSSARIO é lei e onde o roteador é tipado.
 * 3. **Sem `contexto`.** A coluna guarda rota, ids e filtros de exportação; é
 *    diagnóstico, não leitura de tela, e expô-la levaria detalhe de requisição para
 *    dentro de uma superfície que qualquer nível com `auditoria` enxerga. Desde a
 *    DS-711 saem dele só dois recortes escolhidos pela leitura: a `origem` do ato e a
 *    `referencia` (o nome do objeto), nunca o jsonb inteiro.
 */
import { z } from 'zod'
import { meta, uuid, uuidDeRota } from './comum.js'

/** Quem agiu. `usuario` tem `atorId`; sistema e job, não (a coluna é NULL). */
export const atorDaAuditoria = z.enum(['usuario', 'portal', 'sistema', 'job'])
export type AtorDaAuditoria = z.infer<typeof atorDaAuditoria>

/** As nove ações do catálogo (spec 02 §1.11) — o CHECK do banco cobra as mesmas. */
export const acaoDaAuditoria = z.enum([
  'criar',
  'editar',
  'excluir',
  'aprovar',
  'rejeitar',
  'exportar',
  'login',
  'permissao',
  'estado',
])
export type AcaoDaAuditoria = z.infer<typeof acaoDaAuditoria>

export const linhaDoLogDeAlteracoes = z.object({
  id: z.string(),
  quando: z.string(),
  /** AB056: instante UTC aditivo; quando conserva a representação legada e o CSV. */
  quandoInstante: z.iso.datetime().optional(),
  atorId: uuid.nullable(),
  atorNome: z.string().nullable(),
  atorTipo: atorDaAuditoria,
  entidadeTipo: z.string(),
  entidadeId: uuidDeRota,
  acao: acaoDaAuditoria,
  campo: z.string().nullable(),
  valorDe: z.string().nullable(),
  valorPara: z.string().nullable(),
  /*
    DS-711: o que a LEITURA resolve para a tela, sem reescrever o fato gravado. Os quatro são aditivos e
    `nullish`: a resposta de um servidor anterior não os traz, e a tela volta ao que já mostrava.

    `rotuloDe`/`rotuloPara`: o nome ATUAL do registro que `valorDe`/`valorPara` identificam, nos campos que guardam
    um id (o nível de acesso, o responsável do pedido). Nulo com o valor preenchido = o registro não existe mais.
  */
  rotuloDe: z.string().nullish(),
  rotuloPara: z.string().nullish(),
  /** O objeto do fato quando não é o registro da linha: o produto marcado no catálogo, a parte do pedido. */
  referencia: z.string().nullish(),
  /**
   * A origem do ato, um código interno curto (`negocios.gerar_pedido`, `catalogo.exigencia_de_autorizacao`),
   * nunca conteúdo. É o único pedaço do `contexto` que sai da tabela, e sai já escolhido pela leitura.
   */
  origem: z.string().nullish(),
})
export type LinhaDoLogDeAlteracoes = z.infer<typeof linhaDoLogDeAlteracoes>

/**
 * Os filtros do painel (spec 03 §2: `?entidade_tipo&entidade_id&ator&de&ate`).
 *
 * Todos opcionais e todos **recorte**, nunca resolução de registro — pela spec 05
 * §11, valor inválido aqui é IGNORADO e o controle volta ao default. Por isso o
 * schema aceita `string` e o controlador descarta o que não é uuid nem data
 * nominal, em vez de responder 400 a quem digitou na barra de endereços.
 */
export const buscaDoLogDeAlteracoes = z.object({
  entidade_tipo: z.string().optional(),
  entidade_id: z.string().optional(),
  ator: z.string().optional(),
  acao: z.string().optional(),
  de: z.string().optional(),
  ate: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
})
export type BuscaDoLogDeAlteracoes = z.infer<typeof buscaDoLogDeAlteracoes>

/** Mesma busca, com o formato obrigatório — a rota de exportação (spec 03 §1.7). */
export const exportacaoDoLogDeAlteracoes = buscaDoLogDeAlteracoes.extend({
  formato: z.literal('csv'),
})
export type ExportacaoDoLogDeAlteracoes = z.infer<typeof exportacaoDoLogDeAlteracoes>

/**
 * As facetas do recorte (spec 03 §1.8): só tipos e atores COM registro na janela.
 * Um select que oferece 42 tipos de entidade quando 3 têm linha faz a pessoa
 * escolher o vazio — a lista sai do dado, não do catálogo.
 */
export const facetasDoLogDeAlteracoes = z.object({
  tiposDeEntidade: z.array(z.object({ valor: z.string(), total: z.number().int() })),
  atores: z.array(z.object({ id: uuid, nome: z.string() })),
})
export type FacetasDoLogDeAlteracoes = z.infer<typeof facetasDoLogDeAlteracoes>

export const respostaDoLogDeAlteracoes = z.object({
  data: z.array(linhaDoLogDeAlteracoes),
  meta: meta.extend({
    /** DS-38: a flag do módulo `auditoria` decide se o botão Exportar existe. */
    podeExportar: z.boolean(),
    filtros: facetasDoLogDeAlteracoes,
  }),
})
export type RespostaDoLogDeAlteracoes = z.infer<typeof respostaDoLogDeAlteracoes>

/**
 * A trilha contextual do registro aberto (spec 03 §2 · spec 4.2 §Trilha · DS-10).
 *
 * A LINHA é a mesma do painel 8.7, e a reutilização é decisão: são duas perguntas
 * diferentes ("o que andou acontecendo no sistema" e "o que aconteceu com este
 * registro") com uma resposta só. Dois formatos para a mesma linha dariam, mais cedo
 * ou mais tarde, dois passados para o mesmo pedido — que é exatamente o que a DS-10
 * existe para impedir.
 *
 * **Sem `meta`, e a ausência é decisão.** A ficha desenha uma TIMELINE, não uma lista
 * paginada: não há página 2 para onde ir. O que ela precisa saber é se está
 * escondendo alguma coisa, e é isso que `total` responde — quando ele é maior que
 * `data.length`, a tela diz quantos mostrou em vez de fingir que aquilo é tudo
 * (DS-99: bloco sem o dado inteiro aparece dizendo o que falta).
 */
export const respostaDaTrilha = z.object({
  data: z.array(linhaDoLogDeAlteracoes),
  total: z.number().int(),
})
export type RespostaDaTrilha = z.infer<typeof respostaDaTrilha>
